import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from '@mui/material'
import { toast } from 'react-toastify'

/**
 * MOBD-66: render compartido del contrato de bloqueo de usuario del backend.
 *
 * El backend redacta el texto del bloqueo (title/reason/solution/cta.label); estas apps de
 * OPERADOR solo lo MUESTRAN verbatim, sin reinterpretarlo. `NormalizedBlock` es exactamente lo que
 * devuelve `resolveBlock(error)` de `blockAction`.
 */
export interface NormalizedBlock {
    code: string | null
    title: string | null
    reason: string | null
    solution: string | null
    ctaAction: string
    ctaLabel: string | null
    retriable: boolean
    audience: string | null
}

// ¿El bloque ofrece una accion accionable? Una NONE sin etiqueta no pinta boton primario.
const hasCta = (block: NormalizedBlock): boolean =>
    (!!block.ctaAction && block.ctaAction !== 'NONE') || !!block.ctaLabel

interface BlockModalProps {
    block: NormalizedBlock | null
    onClose: () => void
    onCta?: () => void
}

// Modal (MUI Dialog): titulo = block.title, cuerpo = reason + solution, boton primario =
// block.ctaLabel (fallback t('block.cta.<ACTION>')), boton secundario = descartar. Si `block` es
// null no pinta nada, para que el consumidor pueda dejar <BlockModal block={resolveBlock(error)} />
// siempre montado.
export function BlockModal({
    block,
    onClose,
    onCta,
}: BlockModalProps): JSX.Element | null {
    const { t } = useTranslation()
    if (!block) return null

    const action = block.ctaAction || 'NONE'
    const ctaLabel = block.ctaLabel || t('block.cta.' + action)
    // MOBD-66: sin handler real el boton primario solo cerraba el aviso: un CTA muerto («Ir a mi perfil») es
    // peor que no ofrecer ninguno. Se pinta unicamente cuando el consumidor aporta la accion.
    const onPrimary = onCta

    return (
        <Dialog open onClose={onClose} aria-labelledby="block-modal-title">
            <DialogTitle id="block-modal-title">
                {block.title || t('block.default.title')}
            </DialogTitle>
            <DialogContent>
                {block.reason && (
                    <DialogContentText>{block.reason}</DialogContentText>
                )}
                {block.solution && (
                    <DialogContentText
                        sx={{ mt: block.reason ? 2 : 0, fontWeight: 600 }}
                    >
                        {block.solution}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('block.dismiss')}</Button>
                {onPrimary && hasCta(block) && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onPrimary}
                    >
                        {ctaLabel}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

// Hook para avisos ligeros (react-toastify) del mismo contrato: titulo + motivo y, si hay accion,
// un boton inline. Se usa donde un modal seria excesivo (una accion de lista, un recalculo...).
export function useBlockToast() {
    const { t } = useTranslation()

    const showBlock = (block: NormalizedBlock | null, onCta?: () => void) => {
        if (!block) return
        const action = block.ctaAction || 'NONE'
        const ctaLabel = block.ctaLabel || t('block.cta.' + action)

        // El id del toast se conoce tras crearlo; el boton (que se pulsa despues) ya lo ve.
        let toastId: number | string | undefined
        const handleCta = () => {
            if (onCta) onCta()
            if (toastId != null) toast.dismiss(toastId)
        }

        toastId = toast.error(
            <div>
                {block.title && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {block.title}
                    </Typography>
                )}
                {block.reason && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {block.reason}
                    </Typography>
                )}
                {/* MOBD-66: el «como resolverlo» tambien en el toast; en company era el unico renderer cableado. */}
                {block.solution && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                        {block.solution}
                    </Typography>
                )}
                {hasCta(block) && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        sx={{ mt: 1 }}
                        onClick={handleCta}
                    >
                        {ctaLabel}
                    </Button>
                )}
            </div>,
            { position: toast.POSITION.TOP_CENTER, autoClose: false }
        )
    }

    return { showBlock }
}
